/**
 * Create Listing — Image & Document Upload Mixin
 *
 * Handles image compression, preview management, document slots.
 *
 * @package Lovasok_Marketplace
 */

import { formatBytes } from '../../utils/formatters.js';
import { compressImage, getFileSizeReduction, canCompressInBrowser } from '../../utils/image-compressor.js';

const MAX_IMAGE_SIZE_BYTES = 25 * 1024 * 1024;
const MIN_COVER_LONG_SIDE = 1200;
const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

function readImageDimensions(file) {
    return new Promise((resolve) => {
        if (!canCompressInBrowser(file)) {
            resolve({ width: 0, height: 0 });
            return;
        }

        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const size = { width: img.naturalWidth || 0, height: img.naturalHeight || 0 };
            URL.revokeObjectURL(url);
            resolve(size);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve({ width: 0, height: 0 });
        };
        img.src = url;
    });
}

export function imagesMixin() {
    return {
        uploadFiles: [],
        uploadDocuments: [],
        previewImages: [],
        previewDocuments: [],
        deletedAttachments: [],
        uploadProgress: '',

        isImageEditingLocked() {
            if (!this.isEditing) return false;
            if (Number(this.listingHighlightTier || 1) > 1) return false;
            // HF→eladó átváltás után a meglévő képek szerkeszthetők maradnak
            if (this.convertedFromHorseface) return false;
            // Valódi feltöltött képek (attachment ID) — ne zároljuk
            if (this.previewImages.some(img => img && img.id)) return false;
            return true;
        },

        async handleImageUpload(event) {
            if (this.isImageEditingLocked()) {
                this.validationErrors = ['Ingyenes hirdetésnél a képek szerkesztése zárolt. Vásárolj kiemelést a képek kezeléséhez.'];
                return;
            }

            const files = Array.from(event.target.files);
            const remainingSlots = 10 - this.previewImages.length;

            if (remainingSlots <= 0) {
                this.validationErrors = ['Maximum 10 kép tölthető fel'];
                return;
            }

            for (const file of files.slice(0, remainingSlots)) {
                const mimeType = (file.type || '').toLowerCase();
                if (!SUPPORTED_IMAGE_TYPES.includes(mimeType)) {
                    this.validationErrors = [`Nem támogatott képformátum: ${file.name}`];
                    continue;
                }
                if (file.size > MAX_IMAGE_SIZE_BYTES) {
                    this.validationErrors = [`A kép túl nagy (${file.name}). Max. 25 MB/kép.`];
                    continue;
                }

                try {
                    let uploadFile = file;
                    const dimensions = await readImageDimensions(file);

                    if (canCompressInBrowser(file)) {
                        this.uploadProgress = `Kép tömörítése: ${file.name}...`;
                        const compressedBlob = await compressImage(file, 1920, 0.85);
                        const stats = getFileSizeReduction(file.size, compressedBlob.size);
                        console.log(`Compressed ${file.name}: ${stats.originalMB}MB → ${stats.compressedMB}MB (${stats.reduction}% reduction)`);

                        const fileName = file.name.replace(/\.[^.]+$/, '.webp');
                        uploadFile = new File([compressedBlob], fileName, { type: 'image/webp' });
                    } else {
                        this.uploadProgress = `Kép előkészítése (szerver oldali WebP konverzió): ${file.name}...`;
                    }

                    this.uploadFiles.push(uploadFile);
                    this.previewImages.push({
                        url: URL.createObjectURL(uploadFile),
                        name: uploadFile.name,
                        width: dimensions.width,
                        height: dimensions.height,
                    });
                } catch (error) {
                    console.error('Image compression failed:', error);
                    this.validationErrors = [`Kép tömörítési hiba: ${file.name} — kérlek próbáld újra.`];
                }
            }

            this.uploadProgress = '';

            if (files.length > remainingSlots) {
                this.validationErrors = ['A képlimit miatt csak az első 10 kép kerülhet feltöltésre'];
            }
        },

        handleDocumentUpload(event, docType = 'general') {
            const docTypeLabels = {
                general:  'Dokumentum',
                xray:     'Röntgen',
                vet:      'Állatorvosi szakvélemény',
                passport: 'Lóútlevél',
                sale:     'Adásvételi szerződés',
            };
            const typeLabel = docTypeLabels[docType] || 'Dokumentum';

            Array.from(event.target.files).forEach(file => {
                this.uploadDocuments.push({ file, docType });
                this.previewDocuments.push({
                    name:      file.name,
                    size:      formatBytes(file.size),
                    docType,
                    typeLabel,
                });
            });
        },

        removeImage(index) {
            if (this.isImageEditingLocked()) {
                this.validationErrors = ['Ingyenes hirdetésnél a képek törlése zárolt.'];
                return;
            }

            const image = this.previewImages[index];

            if (image.id) {
                this.deletedAttachments.push(image.id);
            } else {
                const uploadIndex = this.previewImages.slice(0, index).filter(img => !img.id).length;
                this.uploadFiles.splice(uploadIndex, 1);
            }

            this.previewImages.splice(index, 1);
        },

        setCoverImage(index) {
            if (this.isImageEditingLocked()) {
                this.validationErrors = ['Ingyenes hirdetésnél a borítókép módosítása zárolt.'];
                return;
            }

            if (index <= 0 || index >= this.previewImages.length) return;

            const [selectedPreview] = this.previewImages.splice(index, 1);
            this.previewImages.unshift(selectedPreview);

            const newFileIndex = this.previewImages
                .slice(1, index + 1)
                .filter(img => !img.id).length;

            if (!selectedPreview.id) {
                const [selectedFile] = this.uploadFiles.splice(newFileIndex, 1);
                if (selectedFile) {
                    this.uploadFiles.unshift(selectedFile);
                }
            }
        },

        removeDocument(index) {
            this.uploadDocuments.splice(index, 1);
            this.previewDocuments.splice(index, 1);
        },

        formatBytes(bytes) {
            return formatBytes(bytes);
        },

        coverImageWarning() {
            const cover = this.previewImages[0];
            if (!cover) return '';

            const width = Number(cover.width) || 0;
            const height = Number(cover.height) || 0;
            const longSide = Math.max(width, height);
            if (longSide <= 0 || longSide >= MIN_COVER_LONG_SIDE) return '';

            return `Ez a kép kicsi (${width}×${height}). A hirdetés oldalán elmosódott lesz. Borítónak legalább ${MIN_COVER_LONG_SIDE} px széles fotót válassz.`;
        },
    };
}
